import React, { FC, useRef, useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useMutation } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { BlueButton, Box, Button } from '~Styles';
import { Input } from '~Styles/inputs';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Loader from '~ComponentsImproved/Loader';
import { useBridgeApi, useButton, useI18n, useLogger, useSelector } from '~Hooks';
import * as actions from '../../actions';
import store from '../../store';
import { LOGIN_MUTATION } from './api';
import { LoginMutation, LoginMutationVariables } from '../Login/__queries__';
import ImageCarousel from './Components/ImageCarousel';

import bLogo from '~Assets/Logos/b_logo_black.svg';
import SwitchLanguage from './Components/SwitchLanguage';
import DividerWithText from '~Components/DividerWithText';
import QRLoginButton from './Components/QRLoginButton';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Wrapper = styled(Box)`
  background-color: var(--colorBlueGray1200);
  color: var(--colorWhite);
  height: 100vh;
  width: 100%;
  position: fixed;
  z-index: 99;
`;

const FormSide = styled(Box)`
  flex: 1;
  animation: ${fadeIn} 0.3s ease-in-out 0.1s forwards;
  opacity: 0;
`;

const BLogoImg = styled.img`
  width: 48px;
  height: 48px;
  object-fit: contain;
  filter: invert(1);
`;

const FormContainer = styled.form`
  flex: 1;
  max-width: 400px;
  min-width: 272px;
  width: 100%;
  align-self: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16px;
`;

const ErrorBox = styled(Box)`
  background-color: color-mix(in srgb, var(--colorUIRed) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--colorUIRed) 30%, transparent);
  border-radius: 8px;
  padding: 10px 14px;
  animation: ${fadeIn} 0.2s ease-in-out forwards;
`;

const WarningBox = styled(Box)`
  background-color: color-mix(in srgb, var(--colorUIYellow) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--colorUIYellow) 30%, transparent);
  border-radius: 8px;
  padding: 10px 14px;
  animation: ${fadeIn} 0.2s ease-in-out forwards;
`;

const SubmitButton = styled(BlueButton)`
  max-height: 40px;
`;

interface Props {
  refetch: () => Promise<any>;
}

const Login: FC<Props> = ({ refetch }) => {
  const { i18n } = useI18n();
  const logger = useLogger('login');
  const bridgeApi = useBridgeApi();
  const history = useHistory();

  const authToken = useSelector((state) => state.authToken);

  const API_URL: string = process.env.FEDERATION_URL!;
  const [, env] = API_URL.split('.');

  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [showAccountError, setShowAccountError] = useState<boolean>(false);

  const [login] = useMutation<LoginMutation, LoginMutationVariables>(LOGIN_MUTATION);

  // Show account error if auth token is present but user data fails to load
  useEffect(() => {
    if (authToken) {
      const timeout = setTimeout(() => {
        setShowAccountError(true);
      }, 2000);
      return () => clearTimeout(timeout);
    }

    setShowAccountError(false);
    return;
  }, [authToken]);

  const setToken = useCallback((token: string) => {
    store.dispatch(actions.setToken(token));

    // Reset path to prevent being directed into a venue you dont have access to
    store.dispatch({
      type: actions.SET_LAST_VISITED_PATH,
      path: '',
    });

    // Send token to native app wrappers
    try {
      window.ReactNativeWebView.postMessage(`authToken:${token}`);
    } catch (e) {
      // Not in React Native
    }

    try {
      window.webkit.messageHandlers.reactNative.postMessage({
        data: `authToken:${token}`,
      });
    } catch (e) {
      // Not in iOS WebKit
    }

    setLoading(false);
  }, []);

  const handleSubmit = async () => {
    const username = usernameRef.current?.value?.trim() || '';
    const password = passwordRef.current?.value || '';

    if (!username || !password) {
      setError('Please enter both username and password');
      return;
    }

    logger.info(`Login attempt: ${username}`);

    try {
      setLoading(true);
      setError('');

      const results = await login({
        variables: { username, password },
      });

      if (!results.data?.login) {
        const message =
          results.errors && results.errors.length > 0
            ? results.errors[0].message
            : i18n('App.UnkownError');
        setError(message || '');
        setLoading(false);
        logger.warn(`Login failure: ${username}. Reason: ${message}`);
        return;
      }

      const { error: loginError, token } = results.data.login;

      if (token) {
        // Navigate away from the current route (e.g. a TV kiosk parked on /logout) before the
        // token is set, otherwise the Redux update can render Logout for a beat and wipe it again
        history.push('/venues');
        setToken(token);
        logger.info(`Login success: ${username}`);
        await refetch();
        return;
      }

      // Handle error codes
      let message = '';
      switch (loginError) {
        case 'ERROR_INVALID_CREDENTIALS':
        case 'ERROR_NOT_VALID_USER':
        case 'INVALID_CREDENTIALS':
          message = i18n('App.InvalidCredit');
          break;
        case 'ERROR_NOT_VALID_PHONE_NUMBER':
          message = i18n('App.InvalidPhone');
          break;
        case 'INTERNAL_SERVER_ERROR':
          message = i18n('App.ServerError');
          break;
        default:
          message = i18n('App.UnkownError');
          break;
      }

      logger.warn(`Login failure: ${username}. Reason: ${message}`);
      setError(message);
      setLoading(false);
    } catch (err) {
      logger.warn(`Login catch failure: ${username}. Reason: ${(err as any).message}`);
      setError(i18n('App.InternetError'));
      setLoading(false);
    }
  };

  const submitButton = useButton({
    onClick: () => handleSubmit(),
    disabled: loading,
  });

  const reloadButton = useButton({
    onClick: () => {
      bridgeApi.reload();
    },
  });

  return (
    <Wrapper display="flex" flexDirection="row">
      <FormSide display="flex" flexDirection="column" padding="large">
        <FormContainer
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSubmit();
            }
          }}>
          <Box display="flex" flexDirection="column" alignItems="center" marginBottom="medium">
            <BLogoImg src={bLogo} alt="Baemingo" />
            <Heading level={2} color="var(--colorWhite)" top={12}>
              {env === 'development' ? 'Developer mode' : i18n('Login.WelcomeToBaemingo')}
            </Heading>
          </Box>

          <Input
            ref={usernameRef}
            dark
            type="text"
            placeholder={i18n('Login.EnterUsername')}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            autoFocus
          />

          <Input
            ref={passwordRef}
            dark
            type="password"
            placeholder={i18n('Login.EnterPassword')}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
          />

          <SubmitButton
            as="button"
            type="submit"
            fluid
            size="small"
            {...submitButton}
            marginTop="xs">
            {loading ? <Loader size="sm" color="var(--colorWhite)" /> : i18n('App.Signin')}
          </SubmitButton>

          <DividerWithText />

          <QRLoginButton
            onToken={async (token) => {
              // See handleSubmit: navigate away from the current route before setting the
              // token so a stale /logout URL can't wipe it again mid-navigation
              history.push('/venues');
              setToken(token);
              logger.info('QR login success');
              await refetch();
            }}
          />

          {error && (
            <ErrorBox display="flex" alignItems="center" gap="xs">
              <Paragraph level={5} color="var(--colorUIRed)">
                {error}
              </Paragraph>
            </ErrorBox>
          )}

          {authToken && showAccountError && (
            <WarningBox display="flex" alignItems="center" gap="xs">
              <Paragraph level={5} color="var(--colorUISand)">
                {i18n('Login.UnableToLoad')}
              </Paragraph>

              <Button {...reloadButton} size="small">
                {i18n('KDS.Reload')}
              </Button>
            </WarningBox>
          )}
        </FormContainer>

        <SwitchLanguage />
      </FormSide>

      <ImageCarousel />
    </Wrapper>
  );
};

export default Login;
